// Copyright 2026, Imprivata, Inc.  All rights reserved.

import CopyButton from '@components/copyButton/copyButton';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadCertButton from './DownloadCertButton';

interface SamlMetadataBlockProps {
  idpIssuer: string | null;
  redirectUri: string;
  idpCertificate: string;
  tenantId: string;
}

const TRANSLATION_ROOT = 'apps.app-modal';

interface CopyRowProps {
  label: string;
  value: string;
  confirmText: string;
}

// AC-018 amended / AC-023 (Modal Layout Refresh): each copy row is a
// flex container — label on the left, [icon-only CopyButton, hyperlink]
// on the right. DOM source order inside the affordance group is button
// THEN link so screen readers and the eye both scan copy → value left
// to right. Confirmation after a copy click is announced through a
// per-row visually-hidden aria-live region; the button itself stays
// icon-only with no visible text swap or tooltip.
const CopyRow: React.FC<CopyRowProps> = ({ label, value, confirmText }) => {
  const [announcement, setAnnouncement] = useState('');
  const clearTimer = useRef<ReturnType<typeof setTimeout>>();

  const handleCopy = () => {
    // Clear first so React always sees a state change, even on repeat clicks,
    // ensuring the aria-live region re-announces. The microtask (Promise.resolve)
    // lets the clear render before the re-set, which works correctly inside
    // React Testing Library's act(async) boundary. Auto-clear after 2 s.
    clearTimeout(clearTimer.current);
    setAnnouncement('');
    void Promise.resolve().then(() => {
      setAnnouncement(confirmText);
      clearTimer.current = setTimeout(() => setAnnouncement(''), 2000);
    });
  };

  return (
    <div className="hyperdrive-saml-row">
      <div className="hyperdrive-saml-row-label">{label}</div>
      <div className="hyperdrive-saml-row-affordance">
        <CopyButton textToCopy={value} onClick={handleCopy} />
        {value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        )}
      </div>
      <span className="visually-hidden" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
};

const SamlMetadataBlock: React.FC<SamlMetadataBlockProps> = ({
  idpIssuer,
  redirectUri,
  idpCertificate,
  tenantId,
}) => {
  const { t } = useTranslation();
  const entityIdValue = idpIssuer ?? '';
  const confirmText = t(`${TRANSLATION_ROOT}.copy-confirm`);

  // AC-025 (Batch 17 / F-6): DOM source order is Certificate → Entity ID
  // (issuer) → Redirect URI. The Certificate row leads because download
  // is the most prominent SAML provisioning action in the refreshed
  // design; the two copy rows follow.
  return (
    <div className="saml-metadata-block">
      <strong className="saml-metadata-title">{t(`${TRANSLATION_ROOT}.saml-config-title`)}</strong>

      <div className="hyperdrive-saml-row">
        <div className="hyperdrive-saml-row-label">
          {t(`${TRANSLATION_ROOT}.certificate-label`)}
        </div>
        <div className="hyperdrive-saml-row-affordance">
          <DownloadCertButton idpCertificate={idpCertificate} tenantId={tenantId} />
        </div>
      </div>

      <CopyRow
        label={t(`${TRANSLATION_ROOT}.entity-id-label`)}
        value={entityIdValue}
        confirmText={confirmText}
      />

      <CopyRow
        label={t(`${TRANSLATION_ROOT}.acs-url-label`)}
        value={redirectUri}
        confirmText={confirmText}
      />
    </div>
  );
};

export default SamlMetadataBlock;
